<zef-form>

  @if (!!scalingData()) {
    <div class="__scaling-dialog-layout">

      @if (hasProfiles()) {
        <div class="__profile-field">
          <div class="__profile-field_top">
            <div class="__profile-field_label">Profile</div>
            <select
              class="__profile-field_select"
              [ngModel]="selectedProfileIdLocal()"
              (ngModelChange)="selectProfile($event)">
              @for (profile of sortedProfiles(); track profile.id) {
                <option [value]="profile.id" [title]="profileTitles()[profile.id]">{{ profile.name }}</option>
              }
            </select>
          </div>
          @if (selectedProfile(); as profile) {
            <div class="__profile-field_desc">
              <markdown [data]="profile.description" />
            </div>
          }
        </div>
      }

      <!-- CPU mode toggle -->
      <div class="__cpu-mode-row">
        <h4 class="__cpu-mode-row_label">CPU Mode</h4>
        <div class="__cpu-mode-toggle">
          <button
            type="button"
            class="__cpu-mode-toggle_btn"
            [class.is-active]="explicitCpuMode() === 'SHARED'"
            [class.is-resolved]="!explicitCpuMode() && resolvedDefaultCpuMode() === 'SHARED'"
            (click)="setCpuMode('SHARED')">
            Shared
          </button>
          <button
            type="button"
            class="__cpu-mode-toggle_btn __cpu-mode-toggle_btn--default"
            [class.is-active]="!explicitCpuMode()"
            (click)="setCpuMode(null)">
            Default
          </button>
          <button
            type="button"
            class="__cpu-mode-toggle_btn"
            [class.is-active]="explicitCpuMode() === 'DEDICATED'"
            [class.is-resolved]="!explicitCpuMode() && resolvedDefaultCpuMode() === 'DEDICATED'"
            (click)="setCpuMode('DEDICATED')">
            Dedicated
          </button>
        </div>
      </div>

      @if (cpuModeWillChange()) {
        <div class="__cpu-mode-warning">
          <mat-icon class="__cpu-mode-warning_icon">info_outline</mat-icon>
          <div class="__cpu-mode-warning_text">
            {{ cpuModeChangeDescription() }}.
            The minimum interval between CPU mode changes is 1 hour.
          </div>
        </div>
      }

      <!-- tabs (horizontal) -->
      <div class="__scaling-dialog-tabs-bar">

        @if (hasContainerScaling()) {
          <button
            type="button"
            (click)="changeTab(scalingTypes.Container)"
            [class.is-active]="scalingDataType() === scalingTypes.Container"
            class="__tab-h"
            mat-flat-button>
            @if (overLimitByTab()[scalingTypes.Container]) {
              <span class="__tab-h_over-limit-badge" aria-label="Outside current limits"></span>
            }
            <h4 class="__tab-h_title">Containers</h4>
            <ng-template
              [ngTemplateOutlet]="scalingInfoItemRef"
              [ngTemplateOutletContext]="{
                min: scalingData()[scalingTypes.Container]?.min ?? effectiveHorizontalPlaceholder()?.minContainerCount,
                max: scalingData()[scalingTypes.Container]?.max ?? effectiveHorizontalPlaceholder()?.maxContainerCount,
              }"
            />
          </button>
        }

        <button
          type="button"
          (click)="changeTab(scalingTypes.Cpu)"
          [class.is-active]="scalingDataType() === scalingTypes.Cpu"
          class="__tab-h"
          mat-flat-button>
          @if (overLimitByTab()[scalingTypes.Cpu]) {
            <span class="__tab-h_over-limit-badge" aria-label="Outside current limits"></span>
          }
          <h4 class="__tab-h_title">CPU Cores</h4>
          @if (cpuRamFixedMode()) {
            <ng-template
              [ngTemplateOutlet]="scalingInfoItemRef"
              [ngTemplateOutletContext]="{
                max: scalingData()[scalingTypes.Cpu]?.max ?? effectiveVerticalPlaceholder()?.maxResource?.cpuCoreCount
              }"
            />
          } @else {
            <ng-template
              [ngTemplateOutlet]="scalingInfoItemRef"
              [ngTemplateOutletContext]="{
                min: scalingData()[scalingTypes.Cpu]?.min ?? effectiveVerticalPlaceholder()?.minResource?.cpuCoreCount,
                max: scalingData()[scalingTypes.Cpu]?.max ?? effectiveVerticalPlaceholder()?.maxResource?.cpuCoreCount
              }"
            />
          }
        </button>

        <button
          type="button"
          (click)="changeTab(scalingTypes.Ram)"
          [class.is-active]="scalingDataType() === scalingTypes.Ram"
          class="__tab-h"
          mat-flat-button>
          @if (overLimitByTab()[scalingTypes.Ram]) {
            <span class="__tab-h_over-limit-badge" aria-label="Outside current limits"></span>
          }
          <h4 class="__tab-h_title">RAM</h4>
          @if (cpuRamFixedMode()) {
            <ng-template
              [ngTemplateOutlet]="scalingInfoItemRef"
              [ngTemplateOutletContext]="{
                max: scalingData()[scalingTypes.Ram]?.max ?? effectiveVerticalPlaceholder()?.maxResource?.memoryGBytes,
                suffix: 'GB'
              }"
            />
          } @else {
            <ng-template
              [ngTemplateOutlet]="scalingInfoItemRef"
              [ngTemplateOutletContext]="{
                min: scalingData()[scalingTypes.Ram]?.min ?? effectiveVerticalPlaceholder()?.minResource?.memoryGBytes,
                max: scalingData()[scalingTypes.Ram]?.max ?? effectiveVerticalPlaceholder()?.maxResource.memoryGBytes,
                suffix: 'GB'
              }"
            />
          }
        </button>

        <button
          type="button"
          (click)="changeTab(scalingTypes.Disc)"
          [class.is-active]="scalingDataType() === scalingTypes.Disc"
          class="__tab-h"
          mat-flat-button>
          @if (overLimitByTab()[scalingTypes.Disc]) {
            <span class="__tab-h_over-limit-badge" aria-label="Outside current limits"></span>
          }
          <h4 class="__tab-h_title">Disk</h4>
          <ng-template
            [ngTemplateOutlet]="scalingInfoItemRef"
            [ngTemplateOutletContext]="{
              min: scalingData()[scalingTypes.Disc]?.min ?? effectiveVerticalPlaceholder()?.minResource?.diskGBytes,
              max: scalingData()[scalingTypes.Disc]?.max ?? effectiveVerticalPlaceholder()?.maxResource.diskGBytes,
              suffix: 'GB'
            }"
          />
        </button>

        @if (hasOverridesSchema()) {
          <button
            type="button"
            (click)="selectOverridesTab()"
            [class.is-active]="isOverridesTabActive()"
            class="__tab-h"
            mat-flat-button>
            <h4 class="__tab-h_title">Overrides</h4>
            <div class="__autoscaling-info __autoscaling-info-single">
              <span class="__autoscaling-info_max">
                {{ overridesSetCount() }} <span>/ {{ overridesSchemaSize() }} set</span>
              </span>
            </div>
          </button>
        }

      </div>

      <!-- content -->
      @if (!embedded() || scalingDataType()) {
      <div>
        <zef-scrollbar>

          <!--  TAB: Containers  -->
          @if (scalingDataType() === scalingTypes.Container) {
            <mat-card class="__scaling-dialog-card">

              <div class="__scaling-dialog-card_info">

                <h3 class="__scaling-dialog-card_title">
                  {{ templateContent[serviceStackType()]?.[scalingTypes.Container]?.title }}
                </h3>

                <p
                  class="u-desc __scaling-dialog-card_desc"
                  [innerHTML]="templateContent[serviceStackType()]?.[scalingTypes.Container]?.desc">
                </p>

                <div class="fx:mt-1 mb-1">
                  <button
                    mat-stroked-button
                    type="button"
                    color="accent"
                    class="c-button--smaller"
                    (click)="updateScalingProperty(scalingTypes.Container, 'reset', null)"
                  >Reset scaling to defaults</button>
                </div>

              </div>

              <div class="__scaling-dialog-card_setting">
                @if (hasContainerScaling()) {
                  <ng-template
                    [ngTemplateOutlet]="unifiedDualTemplate"
                    [ngTemplateOutletContext]="{
                      className: 'scaling--container',
                      scalingType: scalingTypes.Container,
                      suffix: scalingTypeSuffixMap[scalingTypes.Container],
                      minLimit: horizontalLimits()?.minContainerCount,
                      maxLimit: horizontalLimits()?.maxContainerCount,
                      showButtonToggle: false,
                      minText: 'Start with',
                      maxText: 'Scale up to'
                    }"
                  />
                }
              </div>

              @if (showHaSuggestion()) {
                <div class="__ha-suggestion">
                  <mat-icon class="__ha-suggestion_icon">warning_amber</mat-icon>
                  <div class="__ha-suggestion_content">
                    <h4 class="__ha-suggestion_title">High Availability</h4>
                    <p class="__ha-suggestion_desc">
                      You can start with 1 container, but you should do so with caution and only for non-critical or dev services.
                      Production grade service should start with at least 2 containers.
                    </p>
                    <button
                      mat-stroked-button
                      type="button"
                      color="accent"
                      class="c-button--smaller"
                      (click)="setHighAvailability()">
                      Set to high availability?
                    </button>
                  </div>
                </div>
              }

            </mat-card>
          }

          <!--  TAB: CPU  -->
          @if (scalingDataType() === scalingTypes.Cpu) {
            <mat-card class="__scaling-dialog-card">

              <div class="__scaling-dialog-card_info">

                <h3 class="__scaling-dialog-card_title">
                  {{ templateContent[serviceStackType()]?.[scalingTypes.Cpu]?.title || 'CPU Scaling' }}
                </h3>

                <p
                  class="u-desc __scaling-dialog-card_desc"
                  [innerHTML]="templateContent[serviceStackType()]?.[scalingTypes.Cpu]?.desc || 'Configure CPU scaling settings.'">
                </p>

                <div class="fx:mt-1 mb-1">
                  <button
                    mat-stroked-button
                    type="button"
                    color="accent"
                    class="c-button--smaller"
                    (click)="updateScalingProperty(scalingTypes.Cpu, 'reset', null)"
                  >Reset scaling to defaults</button>
                </div>

              </div>

              <div class="__scaling-dialog-card_setting">

                @if (!!verticalLimits()?.minResource?.cpuCoreCount && !!verticalLimits()?.maxResource?.cpuCoreCount) {

                  @if (cpuRamFixedMode()) {
                    <ng-template
                      [ngTemplateOutlet]="unifiedSingleTemplate"
                      [ngTemplateOutletContext]="{
                        scalingType: scalingTypes.Cpu,
                        suffix: scalingTypeSuffixMap[scalingTypes.Cpu],
                        minLimit: verticalLimits()?.minResource?.cpuCoreCount,
                        maxLimit: verticalLimits()?.maxResource?.cpuCoreCount,
                        showButtonToggle: false,
                        labelText: 'Set CPU cores between'
                    }" />
                  } @else {
                    <ng-template
                      [ngTemplateOutlet]="unifiedDualTemplate"
                      [ngTemplateOutletContext]="{
                        className: 'scaling--cpu',
                        scalingType: scalingTypes.Cpu,
                        suffix: scalingTypeSuffixMap[scalingTypes.Cpu],
                        minLimit: verticalLimits()?.minResource?.cpuCoreCount,
                        maxLimit: verticalLimits()?.maxResource?.cpuCoreCount,
                        showButtonToggle: false,
                        minText: 'Minimum cores',
                        maxText: 'Maximum cores'
                    }" />
                  }

                }

                @if (!cpuRamFixedMode() && effectiveCpuMode() === 'DEDICATED') {
                  <ng-template [ngTemplateOutlet]="advancedSettingCpu" />
                }

              </div>

            </mat-card>
          }

          <!--  TAB: RAM  -->
          @if (scalingDataType() === scalingTypes.Ram) {
            <mat-card class="__scaling-dialog-card">

              <div class="__scaling-dialog-card_info">

                <h3 class="__scaling-dialog-card_title">
                  {{ templateContent[serviceStackType()]?.[scalingTypes.Ram]?.title || 'RAM Scaling' }}
                </h3>

                <p
                  class="u-desc __scaling-dialog-card_desc"
                  [innerHTML]="templateContent[serviceStackType()]?.[scalingTypes.Ram]?.desc">
                </p>

                <div class="fx:mt-1 mb-1">
                  <button
                    mat-stroked-button
                    type="button"
                    color="accent"
                    class="c-button--smaller"
                    (click)="updateScalingProperty(scalingTypes.Ram, 'reset', null)"
                  >Reset scaling to defaults</button>
                </div>

              </div>

              <div class="__scaling-dialog-card_setting">
                @if (!!verticalLimits()?.maxResource?.memoryGBytes && !!verticalLimits()?.minResource?.memoryGBytes) {

                  @if (cpuRamFixedMode()) {
                    <ng-template
                      [ngTemplateOutlet]="unifiedSingleTemplate"
                      [ngTemplateOutletContext]="{
                        scalingType: scalingTypes.Ram,
                        suffix: scalingTypeSuffixMap[scalingTypes.Ram],
                        minLimit: verticalLimits()?.minResource?.memoryGBytes,
                        maxLimit: verticalLimits()?.maxResource?.memoryGBytes,
                        scalingButtons: scalingButtonValues[scalingTypes.Ram]?.buttons,
                        showButtonToggle: true,
                        labelText: 'Set memory between'
                      }"
                    />
                  } @else {
                    <ng-template
                      [ngTemplateOutlet]="unifiedDualTemplate"
                      [ngTemplateOutletContext]="{
                        className: 'scaling--ram',
                        scalingType: scalingTypes.Ram,
                        suffix: scalingTypeSuffixMap[scalingTypes.Ram],
                        minLimit: verticalLimits()?.minResource?.memoryGBytes,
                        maxLimit: verticalLimits()?.maxResource?.memoryGBytes,
                        scalingButtons: scalingButtonValues[scalingTypes.Ram]?.buttons,
                        showButtonToggle: true,
                        minText: 'Minimum memory',
                        maxText: 'Maximum memory'
                      }"
                    />
                  }
                }
                @if (!cpuRamFixedMode()) {
                  <ng-template [ngTemplateOutlet]="advancedSettingRam" />
                }
              </div>

            </mat-card>
          }

          <!--  TAB: Disc  -->
          @if (scalingDataType() === scalingTypes.Disc) {
            <mat-card class="__scaling-dialog-card">

              <div class="__scaling-dialog-card_info">

                <h3 class="__scaling-dialog-card_title">
                  {{ templateContent[serviceStackType()]?.[scalingTypes.Disc]?.title }}
                </h3>

                <p
                  class="u-desc __scaling-dialog-card_desc"
                  [innerHTML]="templateContent[serviceStackType()]?.[scalingTypes.Disc]?.desc">
                </p>

                <div class="fx:mt-1 mb-1">
                  <button
                    mat-stroked-button
                    type="button"
                    color="accent"
                    class="c-button--smaller"
                    (click)="updateScalingProperty(scalingTypes.Disc, 'reset', null)"
                  >Reset scaling to defaults</button>
                </div>

              </div>

              <div class="__scaling-dialog-card_setting">

                <ng-template
                  [ngTemplateOutlet]="unifiedDualTemplate"
                  [ngTemplateOutletContext]="{
                    className: 'scaling--disc',
                    scalingType: scalingTypes.Disc,
                    suffix: scalingTypeSuffixMap[scalingTypes.Disc],
                    minLimit: verticalLimits()?.minResource?.diskGBytes,
                    maxLimit: verticalLimits()?.maxResource?.diskGBytes,
                    scalingButtons: scalingButtonValues[scalingTypes.Disc]?.buttons,
                    showButtonToggle: true,
                    minText: 'Minimum disk',
                    maxText: 'Maximum disk'
                  }"
                />

              </div>

            </mat-card>
          }

          <!--  TAB: Overrides  -->
          @if (isOverridesTabActive()) {
            <div class="__overrides-tab">
              <zui-profile-overrides-form
                [schema]="selectedProfileOverridesSchema()"
                [ngModel]="currentOverrides()"
                (ngModelChange)="onOverridesChange($event)"
              />
            </div>
          }

        </zef-scrollbar>
      </div>
      }

      @if (hasOverLimitFields()) {
        <div class="__over-limit-banner">
          <mat-icon class="__over-limit-banner_icon">warning_amber</mat-icon>
          <div class="__over-limit-banner_body">
            <div class="__over-limit-banner_head">
              <strong>{{ overLimitFields().length }} values are outside your current limits.</strong>
              <button
                type="button"
                class="__over-limit-banner_toggle"
                (click)="toggleOverLimitDetails()">
                {{ overLimitDetailsOpen() ? 'Hide details' : 'Show details' }}
              </button>
            </div>
            @if (overLimitDetailsOpen()) {
              <div class="__over-limit-banner_details">
                <ul class="__over-limit-banner_list">
                  @for (field of overLimitFields(); track field.scalingType + field.bound) {
                    <li>
                      {{ field.label }} &mdash; entered
                      <code>{{ field.entered }}{{ field.suffix ? ' ' + field.suffix : '' }}</code>,
                      current limit
                      <code>{{ field.limit }}{{ field.suffix ? ' ' + field.suffix : '' }}</code>
                    </li>
                  }
                </ul>
                <p class="__over-limit-banner_explain">
                  The service will run at the current limits.
                  The values you entered are stored as-is.
                </p>
                @if (isFreeUser()) {
                  <p class="__over-limit-banner_explain">
                    If your available limits change later, return here and click <em>Apply changes</em> to activate these values.
                  </p>
                }
              </div>
            }
          </div>
        </div>
      }

    </div>
  }

</zef-form>


<!-- TEMPLATES -->

<ng-template #profileSwitchConfirmTemplate let-data>
  <h2 mat-dialog-title>Switch profile?</h2>
  <mat-dialog-content>
    <p>
      Switching to <strong>{{ data.profileName }}</strong> will clear
      {{ data.droppedKeys.length }}
      override{{ data.droppedKeys.length === 1 ? '' : 's' }}
      that don't apply to it:
    </p>
    <ul class="__profile-switch-confirm_list">
      @for (key of data.droppedKeys; track key) {
        <li><code>{{ key }}</code></li>
      }
    </ul>
  </mat-dialog-content>
  <mat-dialog-actions align="end">
    <button mat-button [mat-dialog-close]="false">Cancel</button>
    <button mat-flat-button color="warn" [mat-dialog-close]="true">Switch and clear</button>
  </mat-dialog-actions>
</ng-template>

<ng-template #advancedSettingCpu>

  <div class="__advanced-settings">

    <zui-separator [size]="'small'" [spacing]="'medium'" />

    <!-- advanced scaling CPU -->
    <div>

      <div class="fx:text-center  fx:pb-1">
        <h4 class="fx:pb-0-5">CPU Scaling Thresholds</h4>
        <p class="u-desc  __scaling-dialog-card_desc  __scaling-dialog-card_desc--cpu">
          Cores scale up based on the higher threshold of single or total core utilization,
          ensuring optimal efficiency and resource management.
        </p>
      </div>

      <div class="fx:grid  fx:grid-cols-1  fx:gap-bu1-5  fx:pt-0-5  __min-free-fields">

        <!-- CPU min free count -->
        <div>
          <zef-form-field class="__min-free-form-field">
            <mat-form-field color="accent" [floatLabel]="'always'">

              <mat-label>Min. Free CPU Cores (%)</mat-label>

              <input
                matInput
                type="number"
                [placeholder]="((scalingData()[scalingTypes.Cpu]?.defaultMinFree * 100) | floor) + ' (default)'"
                [min]="verticalLimits()?.minFreeResource?.cpuCoreCount * 100"
                [max]="(verticalLimits()?.maxResource?.cpuCoreCount * 100) / 1.5"
                [step]="1"
                [ngModel]="scalingData()[scalingTypes.Cpu]?.minFree"
                (ngModelChange)="updateScalingProperty(scalingTypes.Cpu, 'minFreeNum', $event ? $event / 100 : null); updateScalingProperty(scalingTypes.Cpu, 'minFree', $event);"
                autocomplete="off"
              />

              <span class="__input-suffix" matSuffix>%</span>

              <mat-hint class="__hint-text">
                Initiate scale-up when the absolute free capacity drops below a fixed fraction of a single CPU core's capacity, irrespective of the total number of cores. This threshold is set as a fixed percentage of a single core's capacity to maintain a consistent buffer. For example, setting this threshold at 20% (or 0.2 of a core) means that with one core, at least 20% of that core should remain free, and with two cores, at least 10% per core should remain free, thus always reserving the equivalent of 0.2 of a core as free.
              </mat-hint>

            </mat-form-field>
          </zef-form-field>
        </div>

        <!-- CPU min free percent -->
        <div>
          <zef-form-field class="__min-free-form-field" last>
            <mat-form-field color="accent" [floatLabel]="'always'">

              <mat-label>Dynamic Min. Free Total Core Percent</mat-label>

              <input
                matInput
                type="number"
                [min]="0"
                [max]="99"
                [placeholder]="scalingData()[scalingTypes.Cpu]?.defaultMinFreePct + ' (default)'"
                [ngModel]="scalingData()[scalingTypes.Cpu]?.minFreePct"
                (ngModelChange)="updateScalingProperty(scalingTypes.Cpu, 'minFreePct', $event)"
                autocomplete="off"
              />
              <span class="__input-suffix" matSuffix>%</span>

              <mat-hint class="__hint-text">
                Trigger scale-up when the total free capacity across all assigned CPU cores falls below a specified percentage of the total allocated CPU capacity. This percentage should be set based on the desired free capacity relative to the total number of cores in use. For example, setting this to 20% means that the system will scale up when the total free capacity is less than 20% of the combined capacity of all cores. This setting dynamically adjusts as the number of cores changes, ideal for accommodating varying load distributions.
              </mat-hint>

            </mat-form-field>
          </zef-form-field>

        </div>

      </div>

      <zui-separator [size]="'small'" [spacing]="'small'" />

    </div>


    <!-- start core count -->
    <!--
    @if (!cpuRamFixedMode()) {
      <div class="fx:text-center  __start-core-count  fx:pt-0-5">
        <h4 class="fx:pb-0-5">Container Start Core Count</h4>
        <p class="u-desc __scaling-dialog-card_desc">
          How many cores should containers be allocated <br/>when they start to ensure a reliable and fast start
        </p>

        <div>
          <zef-form-field last class="__advanced-short-field">
            <mat-form-field color="accent" [floatLabel]="'always'">

              <mat-label>Start with</mat-label>

              <input
                matInput
                type="number"
                [min]="0"
                [placeholder]="scalingData()[scalingTypes.Cpu]?.defaultStartCount + ' (default)'"
                [max]="verticalLimits()?.maxResource?.cpuCoreCount"
                [ngModel]="scalingData()[scalingTypes.Cpu]?.startCount"
                (ngModelChange)="updateScalingProperty(scalingTypes.Cpu, 'startCount', $event)"
                autocomplete="off"
              />

              <span
                class="__input-suffix"
                matSuffix>cores</span>

            </mat-form-field>
          </zef-form-field>
        </div>

      </div>
    }
    -->

  </div>

</ng-template>

<ng-template #advancedSettingRam>

  <div class="__advanced-settings">

    <zui-separator [size]="'small'" [spacing]="'medium'" />

    <!-- advanced scaling RAM -->
    <div class="fx:text-center  fx:pb-1">
      <h4 class="fx:pb-0-5">RAM Scaling Thresholds</h4>
      <p class="u-desc  __scaling-dialog-card_desc  __scaling-dialog-card_desc--ram">
        Memory scales based on the higher of two thresholds: a fixed minimum (in GB) or a percentage of granted RAM. This ensures efficient resource management, with adjustments made according to current memory demands.
      </p>
    </div>

    <!-- RAM min free -->
    <div class="fx:grid  fx:grid-cols-1  fx:gap-bu1-5  fx:pt-0-5  __min-free-fields">

      <!-- RAM min free count -->
      <div>
        <zef-form-field class="__min-free-form-field">
          <mat-form-field color="accent" [floatLabel]="'always'">

            <mat-label>Minimum Free RAM (GB)</mat-label>

            <input
              matInput
              type="number"
              [min]="verticalLimits()?.minFreeResource?.memoryGBytes"
              [placeholder]="scalingData()[scalingTypes.Ram]?.defaultMinFree + ' (default)'"
              [max]="scalingData()[scalingTypes.Ram]?.max ?? effectiveVerticalPlaceholder()?.maxResource.memoryGBytes"
              [ngModel]="scalingData()[scalingTypes.Ram]?.minFree"
              (ngModelChange)="updateScalingProperty(scalingTypes.Ram, 'minFree', $event)"
              autocomplete="off"
            />

            <span
              class="__input-suffix"
              matSuffix>GB</span>

            <mat-hint class="__hint-text">
              This setting specifies an absolute threshold for free RAM, ensuring that a minimum buffer of free memory is always available. For example, setting this to 200 MB means the system will trigger additional memory resources when available RAM falls below this fixed amount. This setting provides a consistent baseline of free RAM, ideal for maintaining system stability and responsiveness, especially in scenarios with low to moderate memory usage.
            </mat-hint>

          </mat-form-field>
        </zef-form-field>
      </div>

      <!-- RAM min free percent -->
      <div>
        <zef-form-field class="__min-free-form-field" last>
          <mat-form-field color="accent" [floatLabel]="'always'">

            <mat-label>Minimum Free RAM (% of Granted)</mat-label>

            <input
              matInput
              type="number"
              [min]="0"
              [max]="99"
              [step]="1"
              [placeholder]="scalingData()[scalingTypes.Ram]?.defaultMinFreePct + ' (default)'"
              [ngModel]="scalingData()[scalingTypes.Ram]?.minFreePct"
              (ngModelChange)="updateScalingProperty(scalingTypes.Ram, 'minFreePct', $event)"
              autocomplete="off"
            />
            <span
              class="__input-suffix"
              matSuffix>%</span>

            <mat-hint class="__hint-text">
              This setting establishes a dynamic threshold for free RAM based on a percentage of the total granted memory. For instance, setting this to 5% adapts the free memory threshold according to the overall memory allocation. This percentage-based approach ensures that as the total memory usage increases, the buffer also scales up proportionally. For example, with a total RAM of 12 GB, a setting of 5% would reserve a minimum of 600 MB as free. This dynamic adjustment is particularly useful for handling varying loads, ensuring there is always a proportionate amount of free RAM available relative to the total usage.
            </mat-hint>

          </mat-form-field>
        </zef-form-field>
      </div>

    </div>

  </div>

</ng-template>


<ng-template
  #scalingInfoItemRef
  let-min="min"
  let-max="max"
  let-suffix="suffix">
  <div class="__autoscaling-info" [class.__autoscaling-info-single]="!isNumeric(min)">

    @if (isNumeric(min)) {
      <span class="__autoscaling-info_min">
        {{ min }} @if (suffix) { <span>{{ suffix }}</span> }
      </span>
      <mat-icon class="__autoscaling-info_range-icon">height</mat-icon>
    }

    <span class="__autoscaling-info_max">
      {{ max }} @if (suffix) { <span>{{ suffix }}</span> }
    </span>

  </div>
</ng-template>

<ng-template
  #scalingLabel
  let-minLimit="minLimit"
  let-maxLimit="maxLimit"
  let-suffix="suffix"
  let-minText="minText"
  let-maxText="maxText"
  let-separator="separator">
  @if (isNumeric(minLimit)) {
    <span>{{ minText }}
      (<strong>min. {{ minLimit }}
        @if (suffix) {
          <span>{{ suffix }}</span>
        }</strong>)
    </span>
  }
  @if (maxLimit) {
    <span>{{ separator }}{{ maxText }}
      (<strong>max. {{ maxLimit }}
        @if (suffix) {
          <span>{{ suffix }}</span>
        }</strong>)
    </span>
  }
</ng-template>

<!-- UNIFIED DUAL TEMPLATE -->
<ng-template
  #unifiedDualTemplate
  let-scalingType="scalingType"
  let-scalingButtons="scalingButtons"
  let-suffix="suffix"
  let-minLimit="minLimit"
  let-maxLimit="maxLimit"
  let-showButtonToggle="showButtonToggle"
  let-minText="minText"
  let-maxText="maxText"
  let-className="className">

  <div class="__scaling-dialog-card_grid" [class]="className">
    <!-- min -->
    <div>
      <zef-form-field>
        <mat-form-field color="accent">
          <mat-label>
            <ng-template
              [ngTemplateOutlet]="scalingLabel"
              [ngTemplateOutletContext]="{
                minLimit: minLimit,
                suffix: suffix,
                minText: minText || 'Start with',
                separator: ''
              }"
            />
          </mat-label>

          <input
            matInput
            type="number"
            [step]="scalingButtonValues?.[scalingType]?.buttons?.[0]"
            [placeholder]="placeholderOf(scalingData()[scalingType]?.defaultMin)"
            [ngModel]="scalingData()[scalingType]?.min"
            (ngModelChange)="updateScalingProperty(scalingType, 'min', $event)"
            autocomplete="off"
          />
          @if (suffix) {
            <span class="__input-suffix" matSuffix>{{ suffix }}</span>
          }
        </mat-form-field>
      </zef-form-field>

      <div class="__scaling-controls">
        @if (showButtonToggle && scalingButtons) {
          <div>
            <mat-button-toggle-group>
              @for (buttonValue of scalingButtons; track buttonValue; let index = $index) {
                <mat-button-toggle
                  [checked]="index === 0"
                  (click)="changeMinButtonStep(scalingType, buttonValue);"
                  tabindex="-1"
                  [value]="buttonValue">{{ buttonValue }} <span class="__control-button-suffix">{{ suffix }}</span>
                </mat-button-toggle>
              }
            </mat-button-toggle-group>
          </div>
        } @else {
          <div></div>
        }

        <div>
          <!-- MINUS button -->
          <button
            type="button"
            mat-icon-button
            class="__control-button"
            tabindex="-1"
            [disabled]="!buttonStates()[scalingType].canDecrementMin(minLimit)"
            (click)="decrementValue(scalingType, 'min')">
            <mat-icon class="__control-button-icon">remove</mat-icon>
          </button>

          <!-- PLUS button -->
          <button
            type="button"
            mat-icon-button
            class="__control-button"
            tabindex="-1"
            [disabled]="!buttonStates()[scalingType].canIncrementMin(maxLimit)"
            (click)="incrementValue(scalingType, 'min')">
            <mat-icon class="__control-button-icon">add</mat-icon>
          </button>
        </div>
      </div>
    </div>

    <!-- max -->
    <div>
      <zef-form-field>
        <mat-form-field color="accent">
          <mat-label>
            <ng-template
              [ngTemplateOutlet]="scalingLabel"
              [ngTemplateOutletContext]="{
                maxLimit: maxLimit,
                suffix: suffix,
                maxText: maxText || 'Scale up to',
                separator: ''
              }"
            />
          </mat-label>
          <input
            matInput
            type="number"
            [step]="scalingButtonValues?.[scalingType]?.buttons?.[0]"
            [placeholder]="scalingData()[scalingType]?.defaultMax"
            [ngModel]="scalingData()[scalingType]?.max"
            (ngModelChange)="updateScalingProperty(scalingType, 'max', $event)"
            autocomplete="off"
          />
          @if (suffix) {
            <span class="__input-suffix" matSuffix>{{ suffix }}</span>
          }
        </mat-form-field>
      </zef-form-field>

      <div class="__scaling-controls">
        @if (showButtonToggle && scalingButtons) {
          <div>
            <mat-button-toggle-group>
              @for (buttonValue of scalingButtons; track buttonValue; let index = $index) {
                <mat-button-toggle
                  [checked]="index === 0"
                  tabindex="-1"
                  (click)="changeMaxButtonStep(scalingType, buttonValue);"
                  [value]="buttonValue">{{ buttonValue }} <span class="__control-button-suffix">{{ suffix }}</span>
                </mat-button-toggle>
              }
            </mat-button-toggle-group>
          </div>
        } @else {
          <div></div>
        }

        <div>
          <!-- MINUS button -->
          <button
            type="button"
            mat-icon-button
            class="__control-button"
            tabindex="-1"
            [disabled]="!buttonStates()[scalingType].canDecrementMax(minLimit)"
            (click)="decrementValue(scalingType, 'max')">
            <mat-icon class="__control-button-icon">remove</mat-icon>
          </button>

          <!-- PLUS button -->
          <button
            type="button"
            mat-icon-button
            class="__control-button"
            tabindex="-1"
            [disabled]="!buttonStates()[scalingType].canIncrementMax(maxLimit)"
            (click)="incrementValue(scalingType, 'max')">
            <mat-icon class="__control-button-icon">add</mat-icon>
          </button>
        </div>
      </div>
    </div>
  </div>
</ng-template>

<!-- UNIFIED SINGLE TEMPLATE -->
<ng-template
  #unifiedSingleTemplate
  let-scalingType="scalingType"
  let-scalingButtons="scalingButtons"
  let-suffix="suffix"
  let-minLimit="minLimit"
  let-maxLimit="maxLimit"
  let-showButtonToggle="showButtonToggle"
  let-labelText="labelText">

  <div class="__scaling-single-mode">
    <zef-form-field>
      <mat-form-field color="accent">
        <mat-label>
          {{ labelText || 'Set value' }} <strong>{{ minLimit }}</strong> &ndash; <strong>{{ maxLimit }}{{ suffix ? ' ' + suffix : '' }}</strong>
        </mat-label>

        <input
          matInput
          type="number"
          [placeholder]="scalingData()[scalingType]?.defaultMax"
          [ngModel]="scalingData()[scalingType]?.max"
          (ngModelChange)="updateScalingProperty(scalingType, 'singleModel', $event)"
          autocomplete="off"
        />
        @if (suffix) {
          <span class="__input-suffix" matSuffix>{{ suffix }}</span>
        }
      </mat-form-field>
    </zef-form-field>

    <div class="__scaling-controls">
      @if (showButtonToggle && scalingButtons) {
        <div>
          <mat-button-toggle-group>
            @for (buttonValue of scalingButtons; track buttonValue; let index = $index) {
              <mat-button-toggle
                [checked]="index === 0"
                (click)="changeMaxButtonStep(scalingType, buttonValue);"
                [value]="buttonValue">{{ buttonValue }} <span class="__control-button-suffix">{{ suffix }}</span>
              </mat-button-toggle>
            }
          </mat-button-toggle-group>
        </div>
      } @else {
        <div></div>
      }

      <div>
        <!-- MINUS BUTTON -->
        <button
          mat-icon-button
          class="__control-button"
          [disabled]="
            (scalingData()[scalingType].max && scalingData()[scalingType].max - maxToggleButtonValue()[scalingType] < minLimit)
            || (!scalingData()[scalingType].max && scalingData()[scalingType]?.defaultMax - maxToggleButtonValue()[scalingType] < minLimit)
          "
          (click)="decrementValue(scalingType, 'max');">
          <mat-icon class="__control-button-icon">remove</mat-icon>
        </button>

        <!-- PLUS BUTTON -->
        <button
          mat-icon-button
          class="__control-button"
          [disabled]="
            (scalingData()[scalingType].max && scalingData()[scalingType].max + maxToggleButtonValue()[scalingType] > maxLimit)
            || (!scalingData()[scalingType].max && scalingData()[scalingType]?.defaultMax + maxToggleButtonValue()[scalingType] > maxLimit)
          "
          (click)="incrementValue(scalingType, 'max');">
          <mat-icon class="__control-button-icon">add</mat-icon>
        </button>
      </div>
    </div>
  </div>

</ng-template>
